/**
 * Automatic free-gift-with-purchase reconciler (UX layer), multi-gift capable.
 *
 * For EACH active gift config (window.CartIncentives.freeGifts, one per active
 * free-gift discount) it keeps exactly one gift line (quantity 1, carrying the hidden
 * `_free_gift` line-item property whose value is that gift's entry id) in the cart
 * whenever the qualifying subtotal reaches that gift's threshold, and removes it when
 * the cart drops below. Flagged lines matching NO active gift (a discount that was
 * deleted or ended) are removed so no undiscounted "free" line lingers.
 *
 * IMPORTANT: this is presentation only. The actual 100%-off is applied server-side by
 * the Discount Function per discount (the source of truth, also running at checkout).
 * If no free-gift discount is live, the config list is empty and this script does
 * nothing, so a full-price "free" line is never created.
 *
 * The `_free_gift` property keeps each free unit on its own line (Shopify only merges
 * lines with identical variant AND properties), so the shopper's own paid units of the
 * same product stay separate, and two gifts sharing a product stay on distinct lines
 * (their property values differ). The legacy property value "true" is adopted by the
 * matching gift via its variant id.
 */
(function () {
  var incentives = window.CartIncentives || {};
  var routes = incentives.routes || {};

  var GIFT_PROPERTY = '_free_gift';
  var gifts = (incentives.freeGifts || [])
    .filter(function (g) {
      return g && g.id && g.giftVariantId && g.thresholdCents;
    })
    .map(function (g) {
      return {
        id: String(g.id),
        variantNumericId: String(g.giftVariantId).split('/').pop(),
        thresholdCents: g.thresholdCents,
        subtotalBasis: g.subtotalBasis
      };
    });

  if (!gifts.length) return;

  var cartRoute = routes.cart || '/cart';
  var addRoute = routes.cartAdd || '/cart/add';
  var changeRoute = routes.cartChange || '/cart/change';
  // Gifts whose add was rejected (e.g. 422 sold out, common when two gifts share one
  // low-stock product). Skipped for the rest of the page load instead of retrying on
  // every cart event.
  var unavailable = {};

  function getCart() {
    // `cache: 'no-store'` is essential: on return-from-checkout navigations the browser can
    // otherwise serve a cached /cart.js snapshot taken BEFORE the gift line was added, which
    // makes us think the gift is missing and add a duplicate.
    return fetch(cartRoute + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (r) {
      return r.json();
    });
  }

  function isGiftFlagged(item) {
    return Boolean(item.properties && item.properties[GIFT_PROPERTY]);
  }

  // Excludes every flagged gift line (any gift's) and Navidium shipping protection, so
  // no gift or protection fee helps unlock a threshold. Matches the Liquid bar and the
  // discount function.
  function qualifyingSubtotal(cart, basis) {
    var field = basis === 'post_discount' ? 'final_line_price' : 'original_line_price';
    return cart.items.reduce(function (sum, item) {
      var isProtection = String(item.handle || '').indexOf('navidium') !== -1;
      return isGiftFlagged(item) || isProtection ? sum : sum + item[field];
    }, 0);
  }

  // Returns EVERY line belonging to this gift: its variant, flagged with this gift's
  // entry id (or the legacy "true" value, which is adopted by variant match). Untagged
  // lines of the same variant are the shopper's own paid units and must never be touched.
  function giftLinesFor(cart, gift) {
    return cart.items.filter(function (item) {
      if (!isGiftFlagged(item)) return false;
      if (String(item.id) !== gift.variantNumericId) return false;
      var value = String(item.properties[GIFT_PROPERTY]);
      return value === gift.id || value === 'true';
    });
  }

  // Flagged lines that match NO active gift (their discount was deleted or ended).
  function orphanGiftLines(cart) {
    return cart.items.filter(function (item) {
      if (!isGiftFlagged(item)) return false;
      return !gifts.some(function (gift) {
        return giftLinesFor(cart, gift).indexOf(item) !== -1;
      });
    });
  }

  // Sets an arbitrary number of lines to quantity 0, one at a time. Each call resolves
  // the line's current position first, so sequential calls are safe (parallel ones can
  // race the cart lock).
  function zeroLines(lines) {
    return lines.reduce(function (chain, line) {
      return chain.then(function () {
        return setLineQuantity(line, 0);
      });
    }, Promise.resolve());
  }

  // Resolves to true when the gift line was actually added; a rejected add (sold out,
  // unpublished, etc.) marks the gift unavailable so it is not retried until reload.
  function addGift(gift) {
    var body = { id: Number(gift.variantNumericId), quantity: 1, properties: {} };
    body.properties[GIFT_PROPERTY] = gift.id;
    return fetch(addRoute + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        unavailable[gift.id] = true;
        console.log('[free-gift] add rejected for', gift.id, '(status ' + r.status + '); not retrying'); // eslint-disable-line no-console
        return false;
      }
      return true;
    });
  }

  // Addresses the line by its 1-based position, resolved from a fresh /cart.js at call
  // time. Shopify's /cart/change.js rejects a line KEY with "no valid id or line
  // parameter" whenever another line holds the same variant (a shopper who also added
  // the gift product themselves), so keys cannot be relied on. Resolving the position
  // per call also keeps sequential changes correct after a removal shifts the lines.
  function setLineQuantity(line, quantity) {
    return getCart().then(function (cart) {
      var index = cart.items.findIndex(function (item) { return item.key === line.key; });
      if (index === -1) return null; // already gone

      return fetch(changeRoute + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: index + 1, quantity: quantity })
      }).then(function (r) {
        // A rejected change would otherwise fail silently; log it so a gift that
        // lingers or doubles can be traced from the console.
        if (!r.ok) {
          return r.json().catch(function () { return {}; }).then(function (body) {
            console.log('[free-gift] change rejected for line', index + 1, 'to', quantity, '(status ' + r.status + ')', body); // eslint-disable-line no-console
            return r;
          });
        }
        return r;
      });
    });
  }

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    var cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') cartItems.refresh();
    // Raw /cart/*.js calls emit none of the theme's cart events, so tell the other
    // reconciler (cart-addons.js) the cart moved; it decides on a fresh snapshot.
    document.dispatchEvent(new CustomEvent('on:cart:reconciled', { detail: { source: 'free-gift' } }));
  }

  // Reconciles one gift against the cart snapshot. Returns a promise resolving to true
  // when it changed the cart.
  function reconcileGift(cart, gift) {
    var giftLines = giftLinesFor(cart, gift);
    var qualifies = qualifyingSubtotal(cart, gift.subtotalBasis) >= gift.thresholdCents;

    // Below the threshold: remove every line of this gift.
    if (!qualifies) {
      if (giftLines.length) {
        return zeroLines(giftLines).then(function () { return true; });
      }
      return Promise.resolve(false);
    }

    // Qualifies but no gift yet: add exactly one (unless a previous add was rejected).
    if (giftLines.length === 0) {
      if (unavailable[gift.id]) return Promise.resolve(false);
      return addGift(gift);
    }

    // Qualifies and at least one line exists. Keep the first, drop any duplicates
    // (self-heals a doubled-up gift), then make sure the survivor is a single free unit.
    var keep = giftLines[0];
    var extras = giftLines.slice(1);
    if (extras.length) {
      return zeroLines(extras)
        .then(function () {
          return keep.quantity > 1 ? setLineQuantity(keep, 1) : null;
        })
        .then(function () { return true; });
    }
    // Shopper bumped the free line above 1: force it back to a single free unit.
    if (keep.quantity > 1) {
      return setLineQuantity(keep, 1).then(function () { return true; });
    }
    return Promise.resolve(false);
  }

  // Runs through the shared queue (see snippets/cart-incentive-config.liquid) so this
  // never overlaps cart-addons.js: both address lines by position, and two reconcilers
  // acting on their own snapshots at once would shift each other's targets. A run
  // requested while one is already waiting is folded into it.
  var enqueue = (incentives.enqueue) || function (task) { return task(); };
  var queued = false;

  function reconcile() {
    if (queued) return Promise.resolve();
    queued = true;
    return enqueue(function () {
      queued = false;
      return runReconcile();
    });
  }

  function runReconcile() {
    var changed = false;

    return getCart()
      .then(function (cart) {
        // Sequential: cart mutations share a lock, and each change re-resolves its
        // line's position, so every decision can be made from the one snapshot.
        var chain = Promise.resolve();

        var orphans = orphanGiftLines(cart);
        if (orphans.length) {
          changed = true;
          chain = chain.then(function () { return zeroLines(orphans); });
        }

        gifts.forEach(function (gift) {
          chain = chain.then(function () {
            return reconcileGift(cart, gift).then(function (giftChanged) {
              if (giftChanged) changed = true;
            });
          });
        });

        return chain;
      })
      .catch(function (error) {
        console.log('[free-gift] reconcile failed', error); // eslint-disable-line no-console
      })
      .then(function () {
        // Refreshing announces on:cart:reconciled, which re-runs both reconcilers on a
        // fresh snapshot. That settles: a run that changes nothing announces nothing.
        if (changed) refreshCartUI();
      });
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  var debouncedReconcile = debounce(reconcile, 400);

  // Fires on add-to-cart for a new variant and on any line-item quantity change/removal.
  // The theme only emits on:cart:add when the variant was NOT already present, so we must
  // listen to both to catch every cart mutation.
  document.addEventListener('on:cart:add', debouncedReconcile);
  document.addEventListener('on:line-item:change', debouncedReconcile);
  // The add-on reconciler changed the cart (an engraving unit dropped with its
  // bottle can take the cart below a gift threshold).
  document.addEventListener('on:cart:reconciled', debouncedReconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
